import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SEEDED_EMPTY_QUERY } from "#app/lib/filters/seeded-empty-query";
import {
  Box,
  Button,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  useWidgetViewState,
  type WidgetRenderContext,
} from "@salestrak/ui";
import { AdvancedFilters } from "#app/components/filters/advanced-filters";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import type { FilterState } from "#app/lib/filters/types";
import { useFilters } from "#app/lib/hooks/use-filters";
import { parseWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useDashboardIssueScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { WidgetHeaderOnlyMe } from "./widget-header-only-me";
import { AnalyticsContext, WidgetViewEvent, trackEvent } from "#app/lib/analytics";


/** The config key every `kind: "issueFilters"` settings field writes. */
const FILTERS_KEY = "filters";

/** A saved query as the settings editor stores it: the whole builder state
 *  while any row is active, null otherwise. Run through the config parser so
 *  it serializes exactly as it will read back off the resolved config. */
function storedQuery(state: FilterState, activeCount: number): FilterState | null {
  const query = activeCount > 0 ? state : null;
  return (parseWidgetFilters({ query }).query as FilterState | null | undefined) ?? null;
}

/**
 * Shared header filter control that issue-backed widgets contribute via
 * `headerActions`. It opens on the widget's SAVED filter (Settings → Filters):
 * the builder is seeded from the persisted query, so the chip counts the
 * narrowing the card already has and a press shows the rows behind it.
 *
 * Edits are per-viewer until saved. Once the builder diverges from the seed it
 * publishes its `where` into the kit's `WidgetViewState` and owns the query
 * predicate for this viewer/session (`useWidgetFilters` drops the saved query
 * from `base`, `useScopedWhere` folds the published one in). A viewer who can
 * edit the widget (`onConfigChange` present) also gets Save and Discard at the
 * popover's foot: Save writes the builder state to the same `filters` config
 * the settings editor writes, Discard returns the builder to the saved query.
 * A query saved elsewhere (Settings) re-seeds the builder by remount; a save
 * from here does not, so the popover stays where the reader left it.
 * {@link WidgetHeaderScopeFilters} pairs it with the Only-Me toggle;
 * `entity-list` composes both into its own header with search.
 */
export function WidgetHeaderFilters({ config, onConfigChange }: WidgetRenderContext) {
  const savedFilters = useMemo(() => parseWidgetFilters(config[FILTERS_KEY]), [config]);
  const seedQuery = savedFilters.query as FilterState | null | undefined;
  const seedKey = JSON.stringify(seedQuery ?? null);

  // The builder state is local and seeded once, so a saved query that changed
  // under it reaches it by remount. A save made HERE is the one change that
  // must not remount: the builder already holds that state, and a remount
  // would close the popover under the reader's pointer.
  const ownSeedKey = useRef<string | null>(null);
  const [mount, setMount] = useState({ seedKey, gen: 0 });
  if (mount.seedKey !== seedKey) {
    setMount({
      seedKey,
      gen: seedKey === ownSeedKey.current ? mount.gen : mount.gen + 1,
    });
  }

  const save = useMemo(
    () =>
      onConfigChange
        ? (query: FilterState | null) => {
            ownSeedKey.current = JSON.stringify(query);
            onConfigChange({ [FILTERS_KEY]: { ...savedFilters, query } });
            trackEvent({
              context: AnalyticsContext.WidgetView,
              event: WidgetViewEvent.FilterWidget,
              data: { cleared: query == null },
            });
          }
        : undefined,
    [onConfigChange, savedFilters],
  );
  const discard = useCallback(
    () => setMount((current) => ({ ...current, gen: current.gen + 1 })),
    [],
  );

  return (
    <HeaderFilterPopover
      key={mount.gen}
      seedQuery={seedQuery}
      onSave={save}
      onDiscard={discard}
    />
  );
}

/**
 * The header filter chip with the per-card Only-Me toggle beside it, in the
 * shared cross-view order (quick-filter chip, then Only-Me). For an
 * issue-backed widget whose figures a rep reads as "mine" as often as the
 * team's — the toggle publishes `onlyMe` into `WidgetViewState` and
 * `useScopedWhere` folds it, so the widget's data hook needs nothing new.
 */
export function WidgetHeaderScopeFilters(ctx: WidgetRenderContext) {
  return (
    <Box direction="row" align="center" gap="2xs">
      <WidgetHeaderFilters {...ctx} />
      <WidgetHeaderOnlyMe />
    </Box>
  );
}

function HeaderFilterPopover({
  seedQuery,
  onSave,
  onDiscard,
}: {
  seedQuery: FilterState | null | undefined;
  /** Absent where the viewer cannot edit the widget: edits stay transient. */
  onSave?: ((query: FilterState | null) => void) | undefined;
  onDiscard: () => void;
}) {
  const { patch } = useWidgetViewState();
  const issueScope = useDashboardIssueScope();
  const query = useFilters(issueFilterConfig, {
    mode: "advanced",
    advancedState: seedQuery ?? SEEDED_EMPTY_QUERY,
  });
  const activeCount = query.activeFilterCount;
  // Publishing moves the query predicate from `base` to `filterWhere`, which
  // re-keys every consuming query. While the builder still matches its
  // baseline — the hook's own initial state (the seed after any stored-shape
  // migration), or the state last saved from here — the saved fold is already
  // the right answer, so the header stays silent and a card mount costs no
  // second fetch. Once the header has published it keeps publishing until the
  // next save: returning to the baseline by hand must overwrite the last
  // published `where`, not leave it standing.
  const current = JSON.stringify(query.advancedState);
  const [baseline, setBaseline] = useState(current);
  const published = useRef(false);
  const dirty = current !== baseline;

  useEffect(() => {
    if (!dirty && !published.current) return;
    published.current = true;
    // Active filters publish their built `where`; zero active rows publish an
    // explicit clear (the saved query is lifted, a half-built row narrows nothing).
    patch({ filterWhere: activeCount > 0 ? query.where : null });
  }, [dirty, query.where, activeCount, patch]);

  // On the way out (a discard or a re-seed) the saved query owns the predicate
  // again; a published `where` left in the bag would keep narrowing the body.
  useEffect(() => () => patch({ filterWhere: undefined }), [patch]);

  const clearQuery = useCallback(() => {
    query.clearAll();
    query.addBlock();
  }, [query]);

  const save = useCallback(() => {
    if (!onSave) return;
    onSave(storedQuery(query.advancedState, activeCount));
    // The saved state is the new baseline and the body reads it off config
    // from here, so the transient layer steps back.
    setBaseline(current);
    published.current = false;
    patch({ filterWhere: undefined });
  }, [onSave, query.advancedState, activeCount, current, patch]);

  return (
    <Popover>
      {/* Face uses the shared Button geometry used by sibling header actions,
        * on the `compact` face: a card header gives the chip the width left
        * over beside the title and the "…" menu, and the expanded "1 Filter"
        * pill takes more than that. The tint alone says the card is narrowed
        * — the builder this chip opens states by how much, and owns the
        * clearing. */}
      <PopoverTrigger
        render={
          <FilterToggle
            size="xs"
            iconSize="xs"
            icon="filter"
            compact
            count={activeCount}
            onClear={clearQuery}
            tooltip="Quickly filter your Projects"
            clearTooltip="Clear all filters"
          />
        }
      />
      {/* The builder is an edge-to-edge form; the stock popover viewport insets
        * its content, so the padding is zeroed at the viewport slot. */}
      <PopoverPopup align="end" padding="none" width="builder">
        <Box direction="column" maxHeight={440}>
          <ScrollArea>
            <AdvancedFilters config={issueFilterConfig} filters={query} scope={issueScope} />
          </ScrollArea>
          {dirty && onSave ? (
            // The foot appears with the first edit and only for a viewer whose
            // save would land: Save wears the save-view hue, Discard is quiet.
            <Box direction="row" justify="end" gap="xs" padding="sm" border="top">
              <Button size="xs" variant="ghost" onClick={onDiscard}>
                Discard
              </Button>
              <Button size="xs" color="warning" variant="outline" onClick={save}>
                Save
              </Button>
            </Box>
          ) : null}
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
